import { Modal } from "react-bootstrap";
import { RiCloseLargeFill } from "react-icons/ri";
import { MdCheckCircle, MdCancel } from "react-icons/md";
import { useEffect, useState } from "react";
import moment from "moment";
import toast from "react-hot-toast";
import { depositScreenshotUrl } from "../../../services/DepositServices";

const money = (v) => Number(v || 0).toFixed(2);
const dt = (v) => (v ? moment(v).format("DD MMM YYYY, hh:mm A") : "—");

/** The identifier the player actually paid to, which differs per method. */
const accountIdentifier = (row) => {
  if (row.method === "UPI") return row.account_upi_id || "—";
  if (row.method === "PAYTM" || row.method === "PHONEPE")
    return row.account_mobile || "—";
  if (row.method === "QR") return "QR code";
  return "—";
};

const Row = ({ label, children, strong }) => (
  <div className="dpd-row">
    <span className="dpd-label">{label}</span>
    <span className={`dpd-value ${strong ? "dpd-value-strong" : ""}`}>
      {children}
    </span>
  </div>
);

/**
 * Read-only detail view. Approve/reject stay in DepositActionModal so a
 * confirmation step is never skipped by accident from a browsing screen.
 */
const DepositDetailModal = ({ row, onClose, onAct }) => {
  const [proofUrl, setProofUrl] = useState(null);
  const [proofLoading, setProofLoading] = useState(false);

  useEffect(() => {
    if (!row?.screenshot) return undefined;

    let objectUrl = null;
    let cancelled = false;

    (async () => {
      setProofLoading(true);
      try {
        // The proof endpoint is auth-scoped, so it cannot be an <img src>.
        const res = await fetch(depositScreenshotUrl(row.screenshot), {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("token")}`,
          },
        });
        if (!res.ok) throw new Error("not ok");
        const blob = await res.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setProofUrl(objectUrl);
      } catch (error) {
        if (!cancelled) toast.error("Could not load payment screenshot");
      } finally {
        if (!cancelled) setProofLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [row]);

  if (!row) return null;

  const hasBonus = Number(row.discount_amount) > 0;
  const isPending = row.status === "PENDING";
  const isApproved = row.status === "APPROVED";

  return (
    <Modal show onHide={onClose} backdrop="static" centered size="lg">
      <div className="modal-header border-0 pb-0">
        <h5 className="modal-title">Deposit Details</h5>
        <button type="button" className="btn" onClick={onClose}>
          <RiCloseLargeFill size={20} />
        </button>
      </div>
      <hr className="my-1" />

      <Modal.Body className="dpd-body">
        <div className="dpd-hero">
          <div>
            <div className="dpd-hero-label">Credited Amount</div>
            <div className="dpd-hero-amount">₹{money(row.credited_amount)}</div>
            {hasBonus && (
              <div className="dpd-hero-break">
                ₹{money(row.amount)} + ₹{money(row.discount_amount)} bonus (
                {Number(row.discount_percentage)}%)
              </div>
            )}
          </div>
          <span
            className={`dp-badge dp-badge-${String(row.status).toLowerCase()}`}
          >
            {row.status}
          </span>
        </div>

        <div className="dpd-grid">
          <section className="dpd-section">
            <h6 className="dpd-section-title">Player</h6>
            <Row label="Name">{row.user_name}</Row>
            <Row label="Game ID">{row.game_id || "—"}</Row>
            <Row label="Mobile">{row.mobile || "—"}</Row>
            <Row label="Parent Account">{row.parent_name || "—"}</Row>
          </section>

          <section className="dpd-section">
            <h6 className="dpd-section-title">Payment</h6>
            <Row label="Method">{row.method || "—"}</Row>
            <Row label="Account">{row.account_name || "—"}</Row>
            <Row label="Paid To">{accountIdentifier(row)}</Row>
            <Row label="UTR" strong>
              {row.utr_number ? (
                <span className="dpd-utr">{row.utr_number}</span>
              ) : (
                <span className="dp-no-utr">Not submitted</span>
              )}
            </Row>
            {!!row.utr_submitted_at && (
              <Row label="UTR added">{dt(row.utr_submitted_at)}</Row>
            )}
          </section>

          <section className="dpd-section">
            <h6 className="dpd-section-title">Amount</h6>
            <Row label="Deposit">₹{money(row.amount)}</Row>
            <Row label="Bonus">
              {hasBonus ? (
                <span className="dp-bonus">
                  +₹{money(row.discount_amount)} ({Number(row.discount_percentage)}
                  %)
                </span>
              ) : (
                "—"
              )}
            </Row>
            <Row label="Total Credited" strong>
              ₹{money(row.credited_amount)}
            </Row>
          </section>

          <section className="dpd-section">
            <h6 className="dpd-section-title">Processing</h6>
            <Row label="Requested">{dt(row.created_at)}</Row>
            <Row label="Processed">{dt(row.processed_at)}</Row>
            <Row label="Processed By">{row.processed_by_name || "—"}</Row>
            {isApproved && (
              <Row label="Player Balance">
                {money(row.balance_before)} → <strong>{money(row.balance_after)}</strong>
              </Row>
            )}
          </section>
        </div>

        {!!row.remark && (
          <div className="dpd-note">
            <div className="dpd-note-label">Player note</div>
            <div>{row.remark}</div>
          </div>
        )}

        {!!row.admin_remark && (
          <div
            className={`dpd-note ${row.status === "REJECTED" ? "dpd-note-danger" : ""}`}
          >
            <div className="dpd-note-label">
              {row.status === "REJECTED" ? "Rejection reason" : "Admin remark"}
            </div>
            <div>{row.admin_remark}</div>
          </div>
        )}

        <section className="dpd-section dpd-proof-section">
          <h6 className="dpd-section-title">Payment Screenshot</h6>
          {!row.screenshot ? (
            <div className="dpd-no-proof">
              No screenshot was attached — verify the UTR against your bank
              statement before approving.
            </div>
          ) : proofLoading ? (
            <div className="dpd-no-proof">Loading screenshot…</div>
          ) : proofUrl ? (
            <a
              href={proofUrl}
              target="_blank"
              rel="noreferrer"
              className="dpd-proof-link"
            >
              <img src={proofUrl} alt="Payment proof" className="dpd-proof-img" />
              <span className="dpd-proof-hint">Click to open full size</span>
            </a>
          ) : (
            <div className="dpd-no-proof">Screenshot could not be loaded.</div>
          )}
        </section>
      </Modal.Body>

      <div className="modal-footer border-0">
        <button type="button" className="btn btn-light px-4" onClick={onClose}>
          Close
        </button>
        {isPending && (
          <>
            <button
              type="button"
              className="btn btn-danger px-4"
              onClick={() => onAct(row, "reject")}
            >
              <MdCancel size={16} /> Reject
            </button>
            <button
              type="button"
              className="btn btn-success px-4"
              disabled={!row.utr_number}
              title={
                row.utr_number
                  ? "Approve this deposit"
                  : "UTR not submitted — cannot approve"
              }
              onClick={() => row.utr_number && onAct(row, "approve")}
            >
              <MdCheckCircle size={16} /> Approve
            </button>
          </>
        )}
      </div>
    </Modal>
  );
};

export default DepositDetailModal;
